import React, { useState, useEffect, useCallback } from 'react';
import api, { relationshipAPI, ProductRelationship, channelAPI, Channel } from '../api';
import AttributeManager from './AttributeManager';
import ImageManager from './ImageManager';
import WorkflowManager from './WorkflowManager';
import TaxonomySelector from './TaxonomySelector';
import HSNLookup from './HSNLookup';

interface Product {
  id?: number;
  sku: string;
  name: string;
  description?: string;
  price?: number;
  brand?: string;
  hsn_code?: string;
  status: string;
  workflow_status?: string;
  department_id?: number;
  category_id?: number;
  subcategory_id?: number;
  highlight_stock_alerts?: boolean;
  attributes?: Array<{attribute_id: number; attribute_name: string; value: string}>;
  images?: Array<{
    id: number; 
    image_url: string; 
    image_type: string; 
    alt_text?: string; 
    sort_order: number; 
    is_primary: boolean;
    s3_key?: string;
    original_filename?: string;
    file_size?: number;
    content_type?: string;
    created_at?: string;
  }>;
}

interface ProductFormProps {
  product?: Product | null;
  onSave: () => void;
  onCancel: () => void;
}

const ProductForm: React.FC<ProductFormProps> = ({ product, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    brand: '',
    product_type: 'simple',
    status: 'draft',
    department_id: undefined as number | undefined,
    category_id: undefined as number | undefined,
    subcategory_id: undefined as number | undefined,
    hsn_code: '',
    vendor: '',
    currency: 'INR',
    highlight_stock_alerts: false,
  });
  const [attributes, setAttributes] = useState<Record<number, string>>({});
  const [activeTab, setActiveTab] = useState('basic');
  const [generatedSku, setGeneratedSku] = useState<string | null>(null);

  // BOM state for bundle products
  const [bomItems, setBomItems] = useState<Array<{product_id: number; sku: string; name: string; quantity: number; searchText: string}>>([]);
  const [bomSearchResults, setBomSearchResults] = useState<any[]>([]);
  const [bomSearchIdx, setBomSearchIdx] = useState<number>(-1);
  const [taxonomyError, setTaxonomyError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Related Products state
  const [relationships, setRelationships] = useState<ProductRelationship[]>([]);
  const [relSearchQuery, setRelSearchQuery] = useState('');
  const [relSearchResults, setRelSearchResults] = useState<Array<{ id: number; name: string; sku: string }>>([]);
  const [relSelectedProduct, setRelSelectedProduct] = useState<{ id: number; name: string; sku: string } | null>(null);
  const [relType, setRelType] = useState('cross_sell');
  const [relSectionOpen, setRelSectionOpen] = useState(false);
  const [relLoading, setRelLoading] = useState(false);
  const [relError, setRelError] = useState<string | null>(null);

  // Variant Linking state
  const [variantSectionOpen, setVariantSectionOpen] = useState(false);
  const [variantParent, setVariantParent] = useState<{ id: number; name: string; sku: string } | null>(null);
  const [variantSearchQuery, setVariantSearchQuery] = useState('');
  const [variantSearchResults, setVariantSearchResults] = useState<Array<{ id: number; name: string; sku: string; attributes?: Array<{attribute_name: string; value: string}> }>>([]);
  const [variantLoading, setVariantLoading] = useState(false);

  // Variant (Parent) — size selection for auto-creating children
  const [variantSizes, setVariantSizes] = useState<string[]>([]);
  const [variantSizeInput, setVariantSizeInput] = useState('');
  const commonSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL', 'Free Size'];
  const [variantChildren, setVariantChildren] = useState<Array<{ id: number; name: string; sku: string }>>([]);

  // Channel Publishing state
  const [channels, setChannels] = useState<Channel[]>([]);
  const [productChannels, setProductChannels] = useState<Array<{
    id: number; product_id: number; channel_id: number;
    channel_sku?: string; channel_price?: number; channel_description?: string;
    is_published: boolean; channel_name?: string; channel_type?: string;
  }>>([]);
  const [channelSectionOpen, setChannelSectionOpen] = useState(false);
  const [channelLoading, setChannelLoading] = useState(false);
  const [channelError, setChannelError] = useState<string | null>(null);
  const [publishForms, setPublishForms] = useState<Record<number, {
    channel_sku: string; channel_price: string; channel_description: string;
  }>>({});

  // Debug: Log formData changes
  useEffect(() => {
    console.log('[ProductForm] formData updated:', formData);
  }, [formData]);

  useEffect(() => {
    if (product) {
      // Extract HSN, Vendor, Currency from attributes
      const attrMap: Record<string, string> = {};
      if (product.attributes && Array.isArray(product.attributes)) {
        product.attributes.forEach((a: any) => {
          const name = a.attribute_name || a.attribute?.name || '';
          if (name) attrMap[name.toLowerCase()] = a.value || '';
        });
      }

      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price?.toString() || '',
        brand: product.brand || '',
        product_type: (product as any).product_type || 'simple',
        status: product.status,
        department_id: product.department_id,
        category_id: product.category_id,
        subcategory_id: product.subcategory_id,
        hsn_code: (() => {
          // Prefer the dedicated Product.hsn_code column; fall back to legacy attribute
          const raw = ((product as any).hsn_code
            || attrMap['hsn code'] || attrMap['hsn'] || attrMap['hsncode'] || '') as string;
          // Strip trailing .0 from numeric HSN codes (imported from Excel as float)
          return raw.endsWith('.0') ? raw.slice(0, -2) : raw;
        })(),
        vendor: attrMap['vendor'] || '',
        currency: attrMap['currency'] || 'INR',
        highlight_stock_alerts: (product as any).highlight_stock_alerts ?? false,
      });
      
      // Set generated SKU if editing existing product
      if (product.sku) {
        setGeneratedSku(product.sku);
      }
      
      // Set attributes
      if (product.attributes) {
        const attrMap: Record<number, string> = {};
        product.attributes.forEach(attr => {
          attrMap[attr.attribute_id] = attr.value;
        });
        setAttributes(attrMap);
      }
    }
  }, [product]);

  // Fetch relationships when editing an existing product
  const fetchRelationships = useCallback(async () => {
    if (!product?.id) return;
    try {
      const res = await relationshipAPI.listRelationships(product.id);
      setRelationships(res.data);
    } catch (err) {
      console.error('Error fetching relationships:', err);
    }
  }, [product?.id]);

  useEffect(() => {
    fetchRelationships();
  }, [fetchRelationships]);

  // Fetch variant parent and children for current product
  const fetchVariantInfo = useCallback(async () => {
    if (!product?.id) return;
    try {
      // Get current product full details to check parent_id
      const res = await api.get(`/products/${product.id}`);
      const prod = res.data;
      if (prod.parent_id) {
        // Fetch parent product info
        const parentRes = await api.get(`/products/${prod.parent_id}`);
        setVariantParent({ id: parentRes.data.id, name: parentRes.data.name, sku: parentRes.data.sku });
      } else {
        setVariantParent(null);
      }
      // Fetch children (products whose parent_id = this product)
      const childrenRes = await api.get('/products/', { params: { search_field: 'name', search_operator: 'contains', search_value: '', limit: 200 } });
      const allProducts = childrenRes.data.products || [];
      const children = allProducts.filter((p: any) => p.parent_id === product.id);
      setVariantChildren(children.map((c: any) => ({ id: c.id, name: c.name, sku: c.sku })));
    } catch (err) {
      console.error('Error fetching variant info:', err);
    }
  }, [product?.id]);

  useEffect(() => {
    fetchVariantInfo();
  }, [fetchVariantInfo]);

  // Fetch channels and product channel associations
  const fetchChannels = useCallback(async () => {
    if (!product?.id) return;
    try {
      const [allChannels, prodChannels] = await Promise.all([
        channelAPI.listChannels(),
        channelAPI.listProductChannels(product.id),
      ]);
      setChannels(allChannels.data);
      setProductChannels(prodChannels.data);
    } catch (err) {
      console.error('Error fetching channels:', err);
    }
  }, [product?.id]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handlePublishToChannel = async (channelId: number) => {
    if (!product?.id) return;
    setChannelLoading(true);
    setChannelError(null);
    try {
      const form = publishForms[channelId] || { channel_sku: '', channel_price: '', channel_description: '' };
      await channelAPI.publishProduct(product.id, {
        channel_id: channelId,
        channel_sku: form.channel_sku || undefined,
        channel_price: form.channel_price ? parseFloat(form.channel_price) : undefined,
        channel_description: form.channel_description || undefined,
      });
      await fetchChannels();
      // Clear the form for this channel
      setPublishForms((prev) => {
        const next = { ...prev };
        delete next[channelId];
        return next;
      });
    } catch (err: any) {
      setChannelError(err.response?.data?.detail || 'Failed to publish product');
    } finally {
      setChannelLoading(false);
    }
  };

  const handleUnpublishFromChannel = async (channelId: number) => {
    if (!product?.id) return;
    setChannelLoading(true);
    setChannelError(null);
    try {
      await channelAPI.unpublishProduct(product.id, channelId);
      await fetchChannels();
    } catch (err: any) {
      setChannelError(err.response?.data?.detail || 'Failed to unpublish product');
    } finally {
      setChannelLoading(false);
    }
  };

  const getPublishForm = (channelId: number) =>
    publishForms[channelId] || { channel_sku: '', channel_price: '', channel_description: '' };

  const updatePublishForm = (channelId: number, field: string, value: string) => {
    setPublishForms((prev) => ({
      ...prev,
      [channelId]: { ...getPublishForm(channelId), [field]: value },
    }));
  };

  // Search products for relationship autocomplete
  useEffect(() => {
    if (relSearchQuery.length < 2) {
      setRelSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await relationshipAPI.searchProducts(relSearchQuery);
        const products = (res.data.products || res.data || [])
          .filter((p: any) => p.id !== product?.id)
          .map((p: any) => ({ id: p.id, name: p.name, sku: p.sku }));
        setRelSearchResults(products);
      } catch (err) {
        console.error('Error searching products:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [relSearchQuery, product?.id]);

  // Search products for variant linking
  useEffect(() => {
    if (variantSearchQuery.length < 2) {
      setVariantSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/products/', { params: { search_field: 'sku', search_operator: 'contains', search_value: variantSearchQuery, limit: 10 } });
        const bySku = (res.data.products || []).filter((p: any) => p.id !== product?.id);
        const res2 = await api.get('/products/', { params: { search_field: 'name', search_operator: 'contains', search_value: variantSearchQuery, limit: 10 } });
        const byName = (res2.data.products || []).filter((p: any) => p.id !== product?.id);
        // Deduplicate
        const seen = new Set<number>();
        const combined: Array<{ id: number; name: string; sku: string; attributes?: Array<{attribute_name: string; value: string}> }> = [];
        for (const p of [...bySku, ...byName]) {
          if (!seen.has(p.id)) {
            seen.add(p.id);
            combined.push({ id: p.id, name: p.name, sku: p.sku, attributes: p.attributes });
          }
        }
        setVariantSearchResults(combined.slice(0, 10));
      } catch (err) {
        console.error('Error searching for variant parent:', err);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [variantSearchQuery, product?.id]);

  const handleLinkVariantParent = async (parentProduct: { id: number; name: string; sku: string }) => {
    if (!product?.id) return;
    setVariantLoading(true);
    try {
      await api.put(`/products/${product.id}`, { parent_id: parentProduct.id, is_variant: true });
      setVariantParent(parentProduct);
      setVariantSearchQuery('');
      setVariantSearchResults([]);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to link variant');
    } finally {
      setVariantLoading(false);
    }
  };

  const handleUnlinkVariantParent = async () => {
    if (!product?.id) return;
    setVariantLoading(true);
    try {
      await api.put(`/products/${product.id}`, { parent_id: null, is_variant: false });
      setVariantParent(null);
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unlink variant');
    } finally {
      setVariantLoading(false);
    }
  };

  const handleUnlinkChild = async (childId: number) => {
    setVariantLoading(true);
    try {
      await api.put(`/products/${childId}`, { parent_id: null, is_variant: false });
      setVariantChildren(prev => prev.filter(c => c.id !== childId));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to unlink child');
    } finally {
      setVariantLoading(false);
    }
  };

  const handleAddRelationship = async () => {
    if (!product?.id || !relSelectedProduct) return;
    setRelLoading(true);
    setRelError(null);
    try {
      await relationshipAPI.createRelationship(product.id, relSelectedProduct.id, relType);
      await fetchRelationships();
      setRelSelectedProduct(null);
      setRelSearchQuery('');
      setRelSearchResults([]);
      setRelType('cross_sell');
    } catch (err: any) {
      setRelError(err.response?.data?.detail || 'Failed to add relationship');
    } finally {
      setRelLoading(false);
    }
  };

  const handleDeleteRelationship = async (relationshipId: number) => {
    if (!product?.id) return;
    try {
      await relationshipAPI.deleteRelationship(product.id, relationshipId);
      await fetchRelationships();
    } catch (err: any) {
      setRelError(err.response?.data?.detail || 'Failed to delete relationship');
    }
  };

  const relationshipTypeLabels: Record<string, string> = {
    cross_sell: 'Cross-Sell',
    up_sell: 'Up-Sell',
    substitute: 'Substitute',
    bundle: 'Bundle',
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    setTaxonomyError(null);
    setValidationErrors([]);

    // Validate mandatory fields
    const errors: string[] = [];
    if (!formData.name.trim()) errors.push('Name is required');
    if (!formData.department_id) errors.push('Department is required');
    if (!formData.category_id) errors.push('Category is required');
    if (!formData.price || parseFloat(formData.price) <= 0) errors.push('Price is required and must be greater than 0');
    if (!formData.brand?.trim()) errors.push('Brand is required');
    if (!formData.hsn_code?.trim()) errors.push('HSN/GST Code is required');
    if (formData.hsn_code?.trim() && (formData.hsn_code.trim().length < 6 || formData.hsn_code.trim().length > 8)) errors.push('HSN/GST Code must be 6 to 8 digits');

    if (errors.length > 0) {
      setValidationErrors(errors);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Resolve named attributes (HSN Code, Vendor, Currency) to attribute IDs
    const resolveNamedAttributes = async () => {
      const namedAttrs: Record<string, string> = {};
      if (formData.hsn_code) namedAttrs['HSN Code'] = formData.hsn_code;
      if (formData.vendor) namedAttrs['Vendor'] = formData.vendor;
      if (formData.currency && formData.currency !== 'INR') namedAttrs['Currency'] = formData.currency;
      if (formData.currency === 'INR') namedAttrs['Currency'] = 'INR';
      
      // Fetch attribute definitions to get IDs
      const mergedAttrs = { ...attributes };
      if (Object.keys(namedAttrs).length > 0) {
        try {
          const res = await api.get('/attributes/');
          const allAttrs = Array.isArray(res.data) ? res.data : [];
          for (const [name, value] of Object.entries(namedAttrs)) {
            const attrDef = allAttrs.find((a: any) => a.name.toLowerCase() === name.toLowerCase());
            if (attrDef) {
              mergedAttrs[attrDef.id] = value;
            }
          }
        } catch { /* ignore — named attributes won't be saved */ }
      }
      return mergedAttrs;
    };

    const resolvedAttributes = await resolveNamedAttributes();

    const productData = {
      name: formData.name,
      description: formData.description,
      price: formData.price ? parseFloat(formData.price) : null,
      brand: formData.brand,
      hsn_code: formData.hsn_code?.trim() || null,  // persist to Product.hsn_code column
      product_type: formData.product_type,
      status: formData.status,
      department_id: formData.department_id,
      category_id: formData.category_id,
      subcategory_id: formData.subcategory_id,
      highlight_stock_alerts: formData.highlight_stock_alerts,
      attributes: resolvedAttributes  // also kept as 'HSN Code' attribute for backward compat
    };

    try {
      console.log('Sending product data:', productData);
      if (product?.id) {
        const response = await api.put(`/products/${product.id}`, productData);
        console.log('Product updated:', response.data);
        setGeneratedSku(response.data.sku);
        alert('Product updated successfully!');
      } else {
        const response = await api.post('/products/', productData);
        console.log('Product created:', response.data);
        setGeneratedSku(response.data.sku);
        
        // Save BOM components if this is a bundle product
        if (formData.product_type === 'bundle' && bomItems.length > 0) {
          const productId = response.data.id;
          for (const bomItem of bomItems) {
            if (bomItem.product_id) {
              try {
                await api.post(`/products/${productId}/components`, {
                  product_id: bomItem.product_id,
                  quantity: bomItem.quantity
                });
              } catch (bomError) {
                console.error('Failed to add BOM component:', bomError);
              }
            }
          }
        }
        
        alert(`Product created successfully! Generated SKU: ${response.data.sku}`);
        
        // Auto-create variant children if sizes were selected
        const newProductId = response.data.id;
        const newProductSku = response.data.sku;
        
        if (formData.product_type === 'variant_parent' && variantSizes.length > 0 && newProductId) {
          try {
            const childRes = await api.post(`/products/${newProductId}/create-variants`, {
              sizes: variantSizes,
            });
            const childCount = childRes.data?.created || 0;
            alert(`${childCount} variant child product(s) created for sizes: ${variantSizes.join(', ')}`);
          } catch (varErr: any) {
            console.error('Variant creation failed:', varErr);
            alert(`Parent created but variant children failed: ${varErr.response?.data?.detail || 'Unknown error'}`);
          }
        }

        // Check if this is an undecorated product and prompt to create decorated version
        try {
          // Fetch attribute definitions to find the decoration attribute
          const attrsRes = await api.get('/attributes/');
          const allAttrDefs = Array.isArray(attrsRes.data) ? attrsRes.data : [];
          const decorationAttr = allAttrDefs.find((a: any) => a.name.toLowerCase() === 'decoration');
          
          let hasDecoration = false;
          if (decorationAttr && resolvedAttributes[decorationAttr.id]) {
            const decVal = resolvedAttributes[decorationAttr.id].trim().toLowerCase();
            if (decVal && decVal !== 'undecorated' && decVal !== 'none' && decVal !== 'n/a') {
              hasDecoration = true;
            }
          }
          
          if (!hasDecoration && newProductId) {
            const wantsDecorated = window.confirm(
              `Product "${formData.name}" (${newProductSku}) was created as an undecorated product.\n\n` +
              `Would you like to create a decorated version? The new product will have ${newProductSku} set as its ParentSKU.` +
              (variantSizes.length > 0 ? `\n\nVariant children (${variantSizes.join(', ')}) will also be created for the decorated version.` : '')
            );
            if (wantsDecorated) {
              const dupRes = await api.post(`/products/${newProductId}/duplicate`);
              let decoratedMsg = `Decorated version created: ${dupRes.data.sku}`;
              
              // If parent had variants, create children for the decorated duplicate too
              if (variantSizes.length > 0 && dupRes.data.id) {
                try {
                  const decChildRes = await api.post(`/products/${dupRes.data.id}/create-variants`, {
                    sizes: variantSizes,
                  });
                  decoratedMsg += `\n${decChildRes.data?.created || 0} variant children also created.`;
                } catch (decVarErr) {
                  decoratedMsg += '\n(Variant children creation failed for decorated version)';
                }
              }
              alert(decoratedMsg + '\n\nYou can now edit it to set the decoration type.');
            }
          }
        } catch (decorCheckErr) {
          // Non-critical — don't block the flow
          console.error('Decoration check failed:', decorCheckErr);
        }
      }
      onSave();
    } catch (error: any) {
      console.error('Error saving product:', error);
      console.error('Error details:', error.response?.data);
      
      let errorMessage = 'An unexpected error occurred';
      
      if (error.response?.status === 500) {
        const detail = error.response?.data?.detail || '';
        if (detail.includes('duplicate key') || detail.includes('already exists')) {
          errorMessage = 'A product with this SKU already exists. Please use a different SKU.';
        } else if (detail.includes('constraint')) {
          errorMessage = 'Data validation error. Please check your input values.';
        } else if (detail.includes('Subcategory does not belong to category')) {
          setTaxonomyError('Invalid taxonomy selection: Subcategory does not belong to the selected category');
          errorMessage = 'Invalid taxonomy selection. Please verify your department, category, and subcategory selections.';
        } else if (detail.includes('Category does not belong to department')) {
          setTaxonomyError('Invalid taxonomy selection: Category does not belong to the selected department');
          errorMessage = 'Invalid taxonomy selection. Please verify your department, category, and subcategory selections.';
        } else if (detail.includes('inactive')) {
          setTaxonomyError('One or more selected taxonomy entities are inactive');
          errorMessage = 'Cannot create product: One or more selected taxonomy entities are inactive.';
        } else {
          errorMessage = 'Server error occurred. Please try again or contact support.';
        }
      } else if (error.response?.status === 400) {
        const detail = error.response?.data?.detail || 'Invalid data provided. Please check your input.';
        errorMessage = detail;
        
        // Check for taxonomy validation errors
        if (detail.includes('taxonomy') || detail.includes('Subcategory') || detail.includes('Category') || detail.includes('Department')) {
          setTaxonomyError(detail);
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Product not found. It may have been deleted by another user.';
      } else if (error.response?.status === 403) {
        errorMessage = 'You do not have permission to perform this action.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error: ${errorMessage}`);
    }
  };

  const handleRefresh = () => {
    if (product?.id) {
      onSave(); // This will refresh the product data
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDepartmentChange = (departmentId: number | undefined) => {
    console.log('[ProductForm] handleDepartmentChange called with:', departmentId);
    console.log('[ProductForm] Current formData:', formData);
    setFormData((prevData) => ({
      ...prevData,
      department_id: departmentId,
      category_id: undefined,
      subcategory_id: undefined
    }));
    console.log('[ProductForm] State update queued for department_id:', departmentId);
    setTaxonomyError(null);
  };

  const handleCategoryChange = (categoryId: number | undefined) => {
    console.log('[ProductForm] handleCategoryChange called with:', categoryId);
    setFormData((prevData) => ({
      ...prevData,
      category_id: categoryId,
      subcategory_id: undefined
    }));
    setTaxonomyError(null);
  };

  const handleSubcategoryChange = (subcategoryId: number | undefined) => {
    setFormData((prevData) => ({
      ...prevData,
      subcategory_id: subcategoryId
    }));
    setTaxonomyError(null);
  };

  return (
    <div style={{ maxWidth: '800px', width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h3>{product?.id ? 'Edit Product' : 'Add New Product'}</h3>
      </div>

      {/* Prominent product image */}
      {product?.images && product.images.length > 0 && (
        <div style={{ marginBottom: '1.5rem', textAlign: 'center', padding: '16px', background: '#fafbfc', borderRadius: '8px', border: '1px solid #e8ecf0' }}>
          <img 
            src={product.images[0]?.image_url} 
            alt={product.name || 'Product'}
            style={{ maxWidth: '240px', maxHeight: '200px', objectFit: 'contain', borderRadius: '6px' }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
          />
          {product.images.length > 1 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#64748b' }}>
              +{product.images.length - 1} more image{product.images.length > 2 ? 's' : ''}
            </div>
          )}
        </div>
      )}

      {product?.id && (
        <WorkflowManager
          productId={product.id}
          currentStatus={product.workflow_status || 'draft'}
          onStatusChange={handleRefresh}
        />
      )}

      {/* Tab Navigation */}
      <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {['basic', 'attributes', 'images'].map(tab => (
            <button
              key={tab}
              type="button"
              className={`btn ${activeTab === tab ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab(tab)}
              style={{ borderRadius: '4px 4px 0 0' }}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Basic Information Tab */}
        {activeTab === 'basic' && (
          <div>
            {/* Display generated SKU if available */}
            {generatedSku && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                backgroundColor: '#d1fae5',
                color: '#065f46',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: 'bold'
              }}>
                Generated SKU: {generatedSku}
              </div>
            )}

            {/* Display taxonomy validation errors */}
            {taxonomyError && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                backgroundColor: '#fee2e2',
                color: '#991b1b',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {taxonomyError}
              </div>
            )}

            {/* Display mandatory field validation errors */}
            {validationErrors.length > 0 && (
              <div style={{
                padding: '0.75rem',
                marginBottom: '1rem',
                backgroundColor: '#fee2e2',
                border: '1px solid #fca5a5',
                borderRadius: '6px',
                fontSize: '13px',
                color: '#991b1b'
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>Please fill in all required fields:</div>
                <ul style={{ margin: 0, paddingLeft: '18px' }}>
                  {validationErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Taxonomy Selector Component */}
            <TaxonomySelector
              departmentId={formData.department_id}
              categoryId={formData.category_id}
              subcategoryId={formData.subcategory_id}
              onDepartmentChange={handleDepartmentChange}
              onCategoryChange={handleCategoryChange}
              onSubcategoryChange={handleSubcategoryChange}
              required={true}
            />
            
            <div className="form-group">
              <label className="form-label">Name: <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="form-input"
                style={validationErrors.some(e => e.includes('Name')) ? { borderColor: '#ef4444' } : {}}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Description:</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-textarea"
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Price: <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="number"
                step="0.01"
                name="price"
                value={formData.price}
                onChange={handleChange}
                className="form-input"
                style={validationErrors.some(e => e.includes('Price')) ? { borderColor: '#ef4444' } : {}}
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Brand: <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                name="brand"
                value={formData.brand}
                onChange={handleChange}
                className="form-input"
                style={validationErrors.some(e => e.includes('Brand')) ? { borderColor: '#ef4444' } : {}}
              />
            </div>

            <div className="form-group">
              <label className="form-label">HSN/GST Code: <span style={{ color: '#ef4444' }}>*</span></label>
              <HSNLookup
                value={formData.hsn_code}
                onChange={(code) => setFormData(prev => ({ ...prev, hsn_code: code }))}
                placeholder="Type a code or product (e.g., 4820 or 'notebook')"
                hasError={validationErrors.some(e => e.includes('HSN'))}
              />
              <span style={{ fontSize: '11px', color: '#64748b' }}>Search the HSN/SAC catalog, or type a 6 to 8 digit code</span>
            </div>

            <div className="form-grid-2" style={{ gap: '12px', marginBottom: 0 }}>
              <div className="form-group">
                <label className="form-label">Vendor:</label>
                <input
                  type="text"
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Currency:</label>
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="INR">INR</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Product Type:</label>
              <select
                name="product_type"
                value={formData.product_type}
                onChange={handleChange}
                className="form-select"
              >
                <option value="simple">Simple</option>
                <option value="variant_parent">Variant (Parent)</option>
                <option value="variant_child">Variant SKU (Child)</option>
                <option value="bundle">Bundle / Kit</option>
                <option value="customizable">Customizable</option>
                <option value="service">Service</option>
                <option value="virtual">Virtual</option>
              </select>
            </div>

            {/* Highlight for stock alerts — only flagged products raise Out-of-Stock / Low-Stock alerts */}
            <div
              className="form-group"
              style={{
                background: '#f8f9fa',
                padding: '12px 16px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
              }}
            >
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: 0 }}>
                <input
                  type="checkbox"
                  name="highlight_stock_alerts"
                  checked={formData.highlight_stock_alerts}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, highlight_stock_alerts: e.target.checked }))
                  }
                  style={{ width: '16px', height: '16px', marginTop: '2px', flexShrink: 0, accentColor: '#0d9488', cursor: 'pointer' }}
                />
                <span>
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155', display: 'block' }}>
                    Highlight for stock alerts
                  </span>
                  <span style={{ fontSize: '11px', color: '#64748b' }}>
                    Only highlighted products appear in Out of Stock / Low Stock alerts and the Command Center.
                  </span>
                </span>
              </label>
            </div>

            {/* Size selector for Variant (Parent) — auto-creates children */}
            {formData.product_type === 'variant_parent' && !product?.id && (
              <div className="form-group" style={{ gridColumn: '1 / -1', background: '#f8f9fa', padding: '12px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <label className="form-label" style={{ fontWeight: 600 }}>Sizes (Variant Children will be auto-created):</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {commonSizes.map(size => (
                    <button
                      key={size}
                      type="button"
                      onClick={() => setVariantSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size])}
                      style={{
                        padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                        border: variantSizes.includes(size) ? '2px solid #0d9488' : '1px solid #e2e8f0',
                        background: variantSizes.includes(size) ? '#d1fae5' : 'white',
                        color: variantSizes.includes(size) ? '#065f46' : '#64748b',
                      }}
                    >
                      {size}
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="text"
                    value={variantSizeInput}
                    onChange={(e) => setVariantSizeInput(e.target.value)}
                    placeholder="Add custom size..."
                    style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', width: '150px' }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && variantSizeInput.trim()) {
                        e.preventDefault();
                        if (!variantSizes.includes(variantSizeInput.trim())) {
                          setVariantSizes([...variantSizes, variantSizeInput.trim()]);
                        }
                        setVariantSizeInput('');
                      }
                    }}
                  />
                  <button type="button" onClick={() => { if (variantSizeInput.trim() && !variantSizes.includes(variantSizeInput.trim())) { setVariantSizes([...variantSizes, variantSizeInput.trim()]); setVariantSizeInput(''); } }} style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', background: 'white' }}>Add</button>
                </div>
                {variantSizes.length > 0 && (
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#0d9488', fontWeight: 500 }}>
                    {variantSizes.length} size(s) selected — {variantSizes.length} variant child product(s) will be created automatically
                  </div>
                )}
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Status:</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* Bundle BOM - show when product_type is bundle */}
            {formData.product_type === 'bundle' && (
              <div style={{ marginTop: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <label className="form-label" style={{ margin: 0 }}>Bundle Components (BOM)</label>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => setBomItems([...bomItems, { product_id: 0, sku: '', name: '', quantity: 1, searchText: '' }])}
                  >
                    + Add Component
                  </button>
                </div>
                {bomItems.length === 0 && (
                  <div style={{ padding: '12px', textAlign: 'center', color: '#94a3b8', fontSize: '13px', background: '#f8fafc', borderRadius: '4px', border: '1px dashed #cbd5e1' }}>
                    No components added. Click "Add Component" to define the kit contents.
                  </div>
                )}
                {bomItems.map((bomItem, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '3fr 80px 40px', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        placeholder="Search SKU or product name..."
                        value={bomItem.searchText}
                        onChange={(e) => {
                          const val = e.target.value;
                          const newBom = [...bomItems];
                          newBom[idx] = { ...newBom[idx], searchText: val };
                          setBomItems(newBom);
                          // Search products
                          if (val.length >= 2) {
                            api.get('/products/', { params: { q: val, limit: 10 } }).then(res => {
                              setBomSearchResults(res.data.products || []);
                              setBomSearchIdx(idx);
                            });
                          } else {
                            setBomSearchResults([]);
                          }
                        }}
                        onFocus={() => setBomSearchIdx(idx)}
                        style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '13px' }}
                      />
                      {bomSearchIdx === idx && bomSearchResults.length > 0 && bomItem.searchText.length >= 2 && !bomItem.product_id && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e2e8f0', borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '160px', overflow: 'auto' }}>
                          {bomSearchResults.map((p: any) => {
                            const color = p.attributes?.find((a: any) => a.attribute_name.toLowerCase() === 'color')?.value || '';
                            const decoration = p.attributes?.find((a: any) => a.attribute_name.toLowerCase() === 'decoration')?.value || '';
                            const label = `${p.sku} - ${p.name}${color ? ` - ${color}` : ''}${decoration ? ` - ${decoration}` : ''}`;
                            return (
                            <div
                              key={p.id}
                              onMouseDown={() => {
                                const newBom = [...bomItems];
                                newBom[idx] = { ...newBom[idx], product_id: p.id, sku: p.sku, name: p.name, searchText: label };
                                setBomItems(newBom);
                                setBomSearchResults([]);
                              }}
                              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '12px', borderBottom: '1px solid #f1f5f9' }}
                              onMouseOver={(e) => (e.currentTarget.style.background = '#f1f5f9')}
                              onMouseOut={(e) => (e.currentTarget.style.background = 'white')}
                            >
                              <span style={{ fontFamily: 'monospace', color: '#64748b' }}>{p.sku}</span> - {p.name}
                              {color && <span style={{ color: '#64748b' }}> - {color}</span>}
                              {decoration && <span style={{ color: '#64748b' }}> - {decoration}</span>}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <input
                      type="number"
                      min={1}
                      value={bomItem.quantity}
                      onChange={(e) => {
                        const newBom = [...bomItems];
                        newBom[idx] = { ...newBom[idx], quantity: parseInt(e.target.value) || 1 };
                        setBomItems(newBom);
                      }}
                      placeholder="Qty"
                      style={{ width: '100%', padding: '7px 10px', border: '1px solid #dde2e8', borderRadius: '5px', fontSize: '13px', textAlign: 'center' }}
                    />
                    <button
                      type="button"
                      onClick={() => setBomItems(bomItems.filter((_, i) => i !== idx))}
                      style={{ padding: '6px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Attributes Tab */}
        {activeTab === 'attributes' && (
          <AttributeManager
            productId={product?.id}
            categoryId={formData.subcategory_id}
            attributes={attributes}
            onAttributesChange={setAttributes}
          />
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div>
            {product?.id ? (
              <ImageManager
                productId={product.id}
                images={product.images || []}
                onImagesChange={handleRefresh}
              />
            ) : (
              <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: '#666',
                border: '2px dashed #ddd',
                borderRadius: '8px'
              }}>
                <p>Please save the product first to add images.</p>
                <p>Images can be added after the product is created.</p>
              </div>
            )}
          </div>
        )}

        {/* Variant Linking Section - only in edit mode */}
        {product?.id && (
          <div style={{ marginTop: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <button
              type="button"
              onClick={() => setVariantSectionOpen(!variantSectionOpen)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#f0fdfa',
                border: 'none',
                borderRadius: variantSectionOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <span style={{ color: '#0d9488' }}>🔗 Link Variant {variantParent ? `(Parent: ${variantParent.sku})` : variantChildren.length > 0 ? `(${variantChildren.length} children)` : ''}</span>
              <span>{variantSectionOpen ? '▲' : '▼'}</span>
            </button>

            {variantSectionOpen && (
              <div style={{ padding: '1rem' }}>
                {/* Current parent info */}
                {variantParent && (
                  <div style={{ marginBottom: '12px', padding: '10px 14px', background: '#f0fdfa', borderRadius: '6px', border: '1px solid #99f6e4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: '2px' }}>Parent Product</div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: '#1e293b' }}>{variantParent.name} <span style={{ color: '#64748b' }}>({variantParent.sku})</span></div>
                    </div>
                    <button
                      type="button"
                      onClick={handleUnlinkVariantParent}
                      disabled={variantLoading}
                      style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Unlink
                    </button>
                  </div>
                )}

                {/* Set parent - only if no parent set */}
                {!variantParent && (
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                      Set Parent Product (this product becomes a variant)
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={variantSearchQuery}
                        onChange={(e) => setVariantSearchQuery(e.target.value)}
                        placeholder="Search by SKU or name..."
                        className="form-input"
                        style={{ width: '100%' }}
                      />
                      {variantSearchResults.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0,
                          background: 'white', border: '1px solid #e2e8f0', borderRadius: '0 0 6px 6px',
                          maxHeight: '200px', overflowY: 'auto', zIndex: 10,
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        }}>
                          {variantSearchResults.map((p) => {
                            const color = p.attributes?.find((a: any) => a.attribute_name?.toLowerCase() === 'color')?.value || '';
                            const decoration = p.attributes?.find((a: any) => a.attribute_name?.toLowerCase() === 'decoration')?.value || '';
                            return (
                            <div
                              key={p.id}
                              onClick={() => handleLinkVariantParent(p)}
                              style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0', fontSize: '13px' }}
                              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f0fdfa')}
                              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                            >
                              <strong>{p.sku}</strong> — {p.name}
                              {color && <span style={{ color: '#64748b' }}> - {color}</span>}
                              {decoration && <span style={{ color: '#64748b' }}> - {decoration}</span>}
                            </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>
                      Linking a parent makes this product a variant (grouped under the parent in the catalog)
                    </div>
                  </div>
                )}

                {/* Children list */}
                {variantChildren.length > 0 && (
                  <div>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '6px' }}>
                      Child Variants ({variantChildren.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {variantChildren.map(child => (
                        <div key={child.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8fafb', borderRadius: '4px', fontSize: '13px' }}>
                          <span><strong>{child.sku}</strong> — {child.name}</span>
                          <button
                            type="button"
                            onClick={() => handleUnlinkChild(child.id)}
                            disabled={variantLoading}
                            style={{ background: 'none', border: '1px solid #dc2626', color: '#dc2626', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontSize: '11px' }}
                          >
                            Unlink
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!variantParent && variantChildren.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '12px', color: '#94a3b8', fontSize: '13px' }}>
                    No variant links. Search above to set a parent product.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Related Products Section - only in edit mode */}
        {product?.id && (
          <div style={{ marginTop: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <button
              type="button"
              onClick={() => setRelSectionOpen(!relSectionOpen)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#f7fafc',
                border: 'none',
                borderRadius: relSectionOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <span>Related Products ({relationships.length})</span>
              <span>{relSectionOpen ? '▲' : '▼'}</span>
            </button>

            {relSectionOpen && (
              <div style={{ padding: '1rem' }}>
                {relError && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    marginBottom: '0.75rem',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}>
                    {relError}
                  </div>
                )}

                {/* Add relationship form */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                      Search Product
                    </label>
                    <input
                      type="text"
                      value={relSelectedProduct ? `${relSelectedProduct.name} (${relSelectedProduct.sku})` : relSearchQuery}
                      onChange={(e) => {
                        setRelSearchQuery(e.target.value);
                        setRelSelectedProduct(null);
                      }}
                      placeholder="Type to search products..."
                      className="form-input"
                      style={{ width: '100%' }}
                    />
                    {relSearchResults.length > 0 && !relSelectedProduct && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '0 0 4px 4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 10,
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      }}>
                        {relSearchResults.map((p) => (
                          <div
                            key={p.id}
                            onClick={() => {
                              setRelSelectedProduct(p);
                              setRelSearchResults([]);
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f0f0f0',
                              fontSize: '13px',
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7fafc')}
                            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'white')}
                          >
                            <strong>{p.name}</strong> <span style={{ color: '#666' }}>({p.sku})</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={{ minWidth: '140px' }}>
                    <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, marginBottom: '4px' }}>
                      Relationship Type
                    </label>
                    <select
                      value={relType}
                      onChange={(e) => setRelType(e.target.value)}
                      className="form-select"
                      style={{ width: '100%' }}
                    >
                      <option value="cross_sell">Cross-Sell</option>
                      <option value="up_sell">Up-Sell</option>
                      <option value="substitute">Substitute</option>
                      <option value="bundle">Bundle</option>
                    </select>
                  </div>

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleAddRelationship}
                    disabled={!relSelectedProduct || relLoading}
                    style={{ height: '36px' }}
                  >
                    {relLoading ? 'Adding...' : 'Add'}
                  </button>
                </div>

                {/* Relationships table */}
                {relationships.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '1rem 0' }}>
                    No related products yet.
                  </p>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Product</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>SKU</th>
                        <th style={{ textAlign: 'left', padding: '8px 6px' }}>Type</th>
                        <th style={{ textAlign: 'right', padding: '8px 6px' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {relationships.map((rel) => (
                        <tr key={rel.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 6px' }}>{rel.related_product_name || '—'}</td>
                          <td style={{ padding: '8px 6px', color: '#666' }}>{rel.related_product_sku || '—'}</td>
                          <td style={{ padding: '8px 6px' }}>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              backgroundColor: '#e2e8f0',
                            }}>
                              {relationshipTypeLabels[rel.relationship_type] || rel.relationship_type}
                            </span>
                          </td>
                          <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                            <button
                              type="button"
                              onClick={() => handleDeleteRelationship(rel.id)}
                              style={{
                                background: 'none',
                                border: '1px solid #e53e3e',
                                color: '#e53e3e',
                                borderRadius: '4px',
                                padding: '2px 10px',
                                cursor: 'pointer',
                                fontSize: '12px',
                              }}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        )}

        {/* Channels Section - only in edit mode */}
        {product?.id && (
          <div style={{ marginTop: '1.5rem', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <button
              type="button"
              onClick={() => setChannelSectionOpen(!channelSectionOpen)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem',
                background: '#f7fafc',
                border: 'none',
                borderRadius: channelSectionOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <span>Channels ({productChannels.length})</span>
              <span>{channelSectionOpen ? '▲' : '▼'}</span>
            </button>

            {channelSectionOpen && (
              <div style={{ padding: '1rem' }}>
                {channelError && (
                  <div style={{
                    padding: '0.5rem 0.75rem',
                    marginBottom: '0.75rem',
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    borderRadius: '4px',
                    fontSize: '13px',
                  }}>
                    {channelError}
                  </div>
                )}

                {channels.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '13px', textAlign: 'center', padding: '1rem 0' }}>
                    No channels available. Create channels in the Channel Manager first.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {channels.map((channel) => {
                      const published = productChannels.find((pc) => pc.channel_id === channel.id);
                      const form = getPublishForm(channel.id);
                      const typeBadgeColors: Record<string, { bg: string; color: string }> = {
                        ecommerce: { bg: '#dbeafe', color: '#1e40af' },
                        marketplace: { bg: '#fef3c7', color: '#92400e' },
                        retail: { bg: '#d1fae5', color: '#065f46' },
                      };
                      const colors = typeBadgeColors[channel.type] || { bg: '#f1f5f9', color: '#475569' };

                      return (
                        <div
                          key={channel.id}
                          style={{
                            border: '1px solid #e2e8f0',
                            borderRadius: '6px',
                            padding: '12px',
                            backgroundColor: published ? '#f0fdf4' : '#fff',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: published ? '0' : '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 600, fontSize: '14px' }}>{channel.name}</span>
                              <span style={{
                                display: 'inline-block',
                                padding: '2px 8px',
                                borderRadius: '12px',
                                fontSize: '11px',
                                fontWeight: 600,
                                background: colors.bg,
                                color: colors.color,
                                textTransform: 'capitalize',
                              }}>
                                {channel.type}
                              </span>
                              {published && (
                                <span style={{
                                  display: 'inline-block',
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 600,
                                  background: '#d1fae5',
                                  color: '#065f46',
                                }}>
                                  Published
                                </span>
                              )}
                            </div>
                            {published ? (
                              <button
                                type="button"
                                onClick={() => handleUnpublishFromChannel(channel.id)}
                                disabled={channelLoading}
                                style={{
                                  background: 'none',
                                  border: '1px solid #e53e3e',
                                  color: '#e53e3e',
                                  borderRadius: '4px',
                                  padding: '4px 12px',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                }}
                              >
                                Unpublish
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => handlePublishToChannel(channel.id)}
                                disabled={channelLoading}
                                style={{ fontSize: '12px', padding: '4px 12px' }}
                              >
                                Publish
                              </button>
                            )}
                          </div>

                          {/* Show channel-specific details for published channels */}
                          {published && (
                            <div style={{ marginTop: '8px', fontSize: '12px', color: '#555', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                              {published.channel_sku && <span>SKU: {published.channel_sku}</span>}
                              {published.channel_price != null && <span>Price: ${published.channel_price.toFixed(2)}</span>}
                              {published.channel_description && <span>Desc: {published.channel_description}</span>}
                            </div>
                          )}

                          {/* Show publish form fields for unpublished channels */}
                          {!published && (
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <div style={{ flex: 1, minWidth: '120px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                                  Channel SKU
                                </label>
                                <input
                                  type="text"
                                  value={form.channel_sku}
                                  onChange={(e) => updatePublishForm(channel.id, 'channel_sku', e.target.value)}
                                  placeholder="Optional"
                                  className="form-input"
                                  style={{ width: '100%', fontSize: '12px', padding: '4px 8px' }}
                                />
                              </div>
                              <div style={{ flex: 1, minWidth: '100px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                                  Channel Price
                                </label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={form.channel_price}
                                  onChange={(e) => updatePublishForm(channel.id, 'channel_price', e.target.value)}
                                  placeholder="Optional"
                                  className="form-input"
                                  style={{ width: '100%', fontSize: '12px', padding: '4px 8px' }}
                                />
                              </div>
                              <div style={{ flex: 2, minWidth: '180px' }}>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '2px' }}>
                                  Channel Description
                                </label>
                                <input
                                  type="text"
                                  value={form.channel_description}
                                  onChange={(e) => updatePublishForm(channel.id, 'channel_description', e.target.value)}
                                  placeholder="Optional"
                                  className="form-input"
                                  style={{ width: '100%', fontSize: '12px', padding: '4px 8px' }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        <div style={{ display: 'flex', gap: '8px', marginTop: '1.5rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
          <button type="submit" className="btn btn-primary">
            {product?.id ? 'Update Product' : 'Create Product'}
          </button>
          <button type="button" className="btn btn-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;